import { Lexer } from 'marked';

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
const decodeEntities = text => text.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (match, entity) => {
  const name = entity.toLowerCase();
  if (name[0] !== '#') return ENTITIES[name] ?? match;
  const point = name[1] === 'x' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
  return point > 0 && point <= 0x10ffff && !(point >= 0xd800 && point <= 0xdfff)
    ? String.fromCodePoint(point) : match;
});
const SILENT_HTML = new Set(['code', 'pre', 'script', 'style', 'svg', 'math', 'picture', 'video', 'audio', 'iframe', 'object']);

// Speech has different semantics from Copy: code and visual embeds are silent,
// including gab-ui gallery JSON. Parse the original Markdown exactly once so
// nested fences, reference images and escaped literal punctuation stay correct.
export default function markdownToSpeechText(content) {
  if (typeof content !== 'string' || !content.trim()) return '';
  const hidden = [];
  const walk = tokens => tokens.map(token => {
    if (token.type === 'html') {
      if (token.block) return walk(Lexer.lexInline(token.text));
      const tag = token.text.match(/^<\s*(\/?)\s*([\w-]+)/);
      if (!tag) return '';
      const name = tag[2].toLowerCase();
      if (SILENT_HTML.has(name)) {
        if (tag[1]) {
          const index = hidden.lastIndexOf(name);
          if (index >= 0) hidden.splice(index);
        } else if (!/\/\s*>$/.test(token.text)) hidden.push(name);
        return ' ';
      }
      return /^(br|p|div|li|h[1-6]|tr|blockquote)$/.test(name) ? '\n' : '';
    }
    switch (token.type) {
      case 'code': case 'codespan': case 'image': return ' ';
      case 'space': case 'br': case 'hr': return '\n';
      case 'list': return token.items.map(item => walk(item.tokens)).join('\n') + '\n';
      case 'table': return [token.header, ...token.rows]
        .map(row => row.map(cell => walk(cell.tokens)).join(' ')).join('\n') + '\n';
      case 'heading': case 'paragraph': case 'blockquote':
        return walk(token.tokens || []) + '\n';
      case 'text': case 'escape': case 'link': case 'strong': case 'em': case 'del':
        return token.tokens ? walk(token.tokens) : hidden.length ? '' : decodeEntities(token.text || '');
      default: return '';
    }
  }).join('');
  return walk(Lexer.lex(content))
    .replace(/[\t ]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
